/*******************************************************************************
* Licensed Materials - Property of HCL
* (c) Copyright HCL Technologies Ltd. 2020, 2023. All Rights Reserved.

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/

import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { EventHandlersManager } from "vr-react-components";
import MultiplayerTooltipManager from "../multiplayer/MultiplayerTooltipManager";
import { setAreControllersConnected, setIsMobile, setVRMode } from "../store/slice/privateSlice";

const Controllers = ({ intersectClasses }) => {
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(setIsMobile(document.querySelector("a-scene").isMobile));
    });

    // Camera ref
    const camera = React.useRef();

    //Selectors
    const areControllersConnected = useSelector((state) => state.private.device.areControllersConnected);
    const isLookControlsLocked = useSelector((state) => state.private.device.isLookControlsLocked);
    const isCooperative = useSelector((state) => state.private.device.cooperative);
    const inVRMode = useSelector((state) => state.private.device.inVRMode);
    const isMobile = useSelector((state) => state.private.device.isMobile);
    const keyboard = useSelector((state) => state.private.keyboard.visible);
    const participants = useSelector((state) => state.room.participants)
    const socketID = useSelector((state)=> state.user.data.socketId)

    const userIndex = participants.findIndex((participant) => participant.socketId === socketID);
    
    const userIndexRef = useRef(userIndex);
    const cameraOptions = {
        "look-controls-enabled": inVRMode && !isLookControlsLocked,
        "wasd-controls-enabled": inVRMode && !keyboard,
    };
    let lastVRPosition = null;
    let lastVRRotation = null;
    const participantsRef = useRef(participants.length);

    useEffect(() => {
        participantsRef.current = participants.length;
        userIndexRef.current = userIndex;
    }, [participants, userIndex]);

    const events = {
        controllerconnected: () => {
            dispatch(setAreControllersConnected(true));
        },
        "enter-vr": () => {
            if (lastVRPosition?.x !== 0 && lastVRPosition?.x !== undefined) {
                camera.current.setAttribute("position", `${lastVRPosition?.x} ${lastVRPosition?.y} ${lastVRPosition?.z}`);
                camera.current.setAttribute("rotation", lastVRRotation);
            }          
            if (participantsRef.current > 0) {
                camera.current.setAttribute("position", `${userIndexRef.current} 1.6 0`);
            }
            dispatch(setVRMode(true));
        },  
        "exit-vr": () => {
            const position = camera.current.getAttribute("position");
            lastVRPosition = { x: position.x, y: position.y, z: position.z };
            lastVRRotation = camera.current.getAttribute("rotation");
            camera.current.setAttribute("position", "0 1.6 0");
            camera.current.setAttribute("rotation", "0 0 0");        
            dispatch(setVRMode(false));
        },
    };

    let rightHandLaser;
    if (areControllersConnected && isCooperative && inVRMode) {
        rightHandLaser = {
            "right-hand": true,
        };
    }

    //Render
    return (
        <a-entity class="Controllers">
            <EventHandlersManager events={events} enabled={true} />
            {!isMobile && !areControllersConnected && (
                <a-entity
                    id="mouse"
                    raycaster={"objects: " + intersectClasses}
                    cursor="rayOrigin: mouse; mouseCursorStylesEnabled: true"
                />
            )}
            <a-camera ref={camera} {...cameraOptions}>
                {isMobile && !areControllersConnected && (
                    <a-entity
                        animation__click="property: scale; startEvents: click; easing: easeInCubic; dur: 150; from: 0.1 0.1 0.1; to: 1 1 1"
                        animation__fusing="property: scale; startEvents: fusing; easing: easeInCubic; dur: 1500; from: 1 1 1; to: 0.1 0.1 0.1"
                        animation__mouseleave="property: scale; startEvents: mouseleave; easing: easeInCubic; dur: 250; to: 1 1 1"
                        cursor="fuse: true;"
                        geometry="primitive: ring; radiusInner: 0.0025; radiusOuter: 0.0035;"
                        material="color: black; shader: flat"
                        position="0 0 -0.3"
                        raycaster={"objects: " + intersectClasses}
                    />
                )}
                {isCooperative && <MultiplayerTooltipManager />}
            </a-camera>

            <a-entity
                id="right-hand-raycaster"
                line="color: cyan"
                laser-controls="hand: right"
                raycaster={"objects: " + intersectClasses}
                {...rightHandLaser}
            />
            <a-entity
                id="left-hand"
                laser-controls="hand: left"
                raycaster={"objects: " + intersectClasses}
            />
        </a-entity>
    );
};

export default Controllers;